/**
 * The project app on the NEW sync stack (build-and-flip).
 *
 * This directory (`src/`) is the clean home of the new project
 * app, built alongside the legacy `apps/project/`. Components migrate in here
 * as we adopt them (so the legacy dir empties toward deletion), with permanent
 * names. At the flip: delete `apps/workspace/`, `git mv apps/workspace-next
 * apps/project`, and point `/:workspaceName/*` here.
 *
 * Data lives in the NEW universe (the subduction server), separate from legacy by
 * design (no classic↔subduction bridge). New projects are born subduction-native.
 *
 * The World and project live in the URL (`/:worldId/:projectId`, see
 * services/world/worldUrl.ts) so a project is addressable: share the link (or
 * open it in another browser) and both tabs join the SAME World and project
 * over the relay. A path with no project restores the last-opened
 * project, falling back to the WORLD's directory (RFC 0261) and only creating
 * one when nothing is registered — previously an empty localStorage silently
 * forked a new empty project. The auth gate is a later slice, so this sits
 * OUTSIDE the legacy `ProjectRoute` gate.
 */

import {
  addProjectToWorld,
  createProject,
  ensureProjectSkillPackages,
  ensureProjectStandingPath,
  ensureWorldHomeProject,
  ensureWorldSkillPackages,
  ensureWorldSoulPath,
  projectWorldProjects,
  resolveProjectMove,
  usesPrivateSessionCatalog,
} from '@mod/core/model'
import { createSharingProject } from '@mod/core/sharing'
import { type ReactNode, useEffect, useState } from 'react'
import { Navigate, useLocation, useNavigate, useParams } from 'react-router-dom'
import {
  TabRepoProvider,
  useTabRepoBoot,
} from '../../services/world/repo/TabRepoProvider'
import { PROJECT_KEY, SUBDUCTION_RELAY } from '../../services/world/sync-server'
import { getOrCreateWorld } from '../../services/world/world'
import {
  adoptWorldFromPath,
  isDocumentSegment,
  readPreviousWorldPointer,
  worldPath,
} from '../../services/world/worldUrl'
import {
  SharingAccountGate,
  useSharingAccountWorld,
} from './SharingAccountGate'
import { WorldShell } from './WorldShell'

/**
 * The project a path with none opens: the last one opened here, else the World's first,
 * else a new one.
 *
 * ONE RESOLUTION PER REPO, shared by every caller. StrictMode runs the effect
 * below twice in dev, and two runs that both find nothing both made a project:
 * a first run showed "Project" twice. Anything that mounts twice would do the
 * same, so the guard is here rather than in the effect.
 */
type Opened = { worldId: string; projectId: string }

/**
 * The World this browser points at did not open, and nothing here can stand in
 * for it. Usually a link to a World this browser has not been let into.
 */
class WorldUnavailable extends Error {
  constructor(readonly worldId: string) {
    super(`World ${worldId} is not available here`)
  }
}
const resolving = new WeakMap<object, Promise<Opened>>()
function resolveProject(repo: ReturnType<typeof useTabRepoBoot>['repo']) {
  let pending = resolving.get(repo)
  if (!pending) {
    pending = findOrCreateProject(repo).finally(() => resolving.delete(repo))
    resolving.set(repo, pending)
  }
  return pending
}

async function findOrCreateProject(
  repo: ReturnType<typeof useTabRepoBoot>['repo'],
): Promise<Opened> {
  // The pointer, which the URL has already set when it names a World. Known
  // before the World document arrives, so it survives the offline path below.
  const { worldId, created } = await getOrCreateWorld(repo)
  let stored: string | null = null
  try {
    stored = localStorage.getItem(PROJECT_KEY)
  } catch {
    // ignore; the World below is the durable answer anyway
  }

  // RFC 0261 — resolve against the World before creating anything. This used
  // to be `stored ?? createProject(...)`, so an empty localStorage silently
  // forked a brand-new empty project and stranded the real one.
  let id = stored
  try {
    await ensureWorldHomeProject(repo, worldId)
    if (id) {
      // A project that moved is opened under its new id, and the stored id
      // is replaced below: opening the old one would load its whole history
      // just to follow it (RFC 0346 3f).
      const world = await repo.find(worldId as never)
      await (
        world as unknown as { whenReady?: () => Promise<void> }
      ).whenReady?.()
      id = resolveProjectMove(world.doc(), id)
      // Adopt whatever this browser already had (or arrived at by link) so
      // the World is a complete list rather than missing the active one.
      await addProjectToWorld(repo, worldId, id)
    } else {
      const handle = await repo.find(worldId as never)
      await (
        handle as unknown as { whenReady?: () => Promise<void> }
      ).whenReady?.()
      const doc = handle.doc() as
        | { projects?: unknown; workspaces?: unknown }
        | undefined
      // Every World is born with a project registry. Without it the document has not
      // arrived, and "no projects" would be a guess: absent is not empty.
      if (!created && !doc?.projects && !doc?.workspaces)
        throw new WorldUnavailable(worldId)
      id = projectWorldProjects(doc)[0]?.projectId ?? null
    }
    if (!id) {
      // Genuinely nothing registered — now creating is the right answer.
      id = createProject(repo, { title: 'Project' }).documentId as string
      await addProjectToWorld(repo, worldId, id, {
        displayName: 'Project',
      })
    }
  } catch {
    // A World made here and unreachable is an offline first run: fall back to
    // the old behaviour rather than blocking the app on a directory lookup. A
    // World that came from elsewhere (a link, another device) is never stood
    // in for by a new project, which would belong to nobody.
    if (!id && !created) throw new WorldUnavailable(worldId)
    id = id ?? (createProject(repo, { title: 'Project' }).documentId as string)
  }
  try {
    localStorage.setItem(PROJECT_KEY, id)
  } catch {
    // best-effort; the project still exists in-repo
  }
  return { worldId, projectId: id }
}

/** `/` or `/:worldId`: restore the last-opened project or create one, then
 *  redirect to its URL. Never renders the project itself — it always hands off
 *  to `/:worldId/:projectId` so there is one canonical, shareable location. */
function ProjectRedirect() {
  const boot = useTabRepoBoot()
  const navigate = useNavigate()
  const accountWorld = useSharingAccountWorld()
  const [error, setError] = useState('')
  // The query rides along: `?space=new` (RFC 0343) and a `?v=` link both mean
  // something to the project page, and dropping them here loses them silently.
  const { search } = useLocation()
  const [unavailable, setUnavailable] = useState(false)

  useEffect(() => {
    let cancelled = false
    const open: Promise<Opened> =
      boot.sharing && accountWorld
        ? navigator.locks.request(
            `mod-first-project:${accountWorld.location.worldId}`,
            async () => {
              const worldId = accountWorld.location.worldId
              await accountWorld.world.whenReady()
              const existing = projectWorldProjects(accountWorld.world.doc())[0]
                ?.projectId
              if (existing) return { worldId, projectId: existing }
              const { project } = await createSharingProject(
                boot.sharing!,
                accountWorld.person,
                { title: 'Project' },
              )
              await addProjectToWorld(boot.repo, worldId, project.documentId, {
                displayName: 'Project',
              })
              await boot.repo.flush()
              return { worldId, projectId: project.documentId as string }
            },
          )
        : resolveProject(boot.repo)
    open.then(
      ({ worldId, projectId }) => {
        if (!cancelled)
          navigate(`${worldPath(worldId, projectId)}${search}`, {
            replace: true,
          })
      },
      (err: unknown) => {
        if (cancelled) return
        if (err instanceof WorldUnavailable) setUnavailable(true)
        else
          setError(
            err instanceof Error ? err.message : 'Could not open project',
          )
      },
    )
    return () => {
      cancelled = true
    }
  }, [boot, accountWorld, navigate, search])

  if (unavailable) return <WorldUnavailableNotice />

  return (
    <div className="flex h-screen items-center justify-center text-muted-foreground text-sm">
      {error || 'Opening project…'}
    </div>
  )
}

/**
 * A World that did not open. The link may name a World this browser has not
 * been let into; the way back is the World it pointed at before the link.
 */
function WorldUnavailableNotice() {
  const previous = readPreviousWorldPointer()
  return (
    <div className="flex h-screen flex-col items-center justify-center gap-2 text-muted-foreground text-sm">
      <p>This World is not available in this browser.</p>
      {previous && (
        <a className="underline" href={worldPath(previous)}>
          Back to your World
        </a>
      )}
    </div>
  )
}

/** `/:worldId/:projectId`: open the project named in the URL. */
function Project({ projectId }: { projectId: string }) {
  const boot = useTabRepoBoot()

  // Remember this as the project `/:worldId` restores next time.
  useEffect(() => {
    try {
      if (!boot.sharing) localStorage.setItem(PROJECT_KEY, projectId)
    } catch {
      // best-effort
    }
  }, [boot.sharing, projectId])

  // RFC 0261 — register it in the World. Opening a SHARED LINK is the common
  // case: without this the project would be reachable only while the url
  // survives in history, and would never appear in this browser's directory.
  // Best-effort — an unregistered project still opens fine.
  useEffect(() => {
    // Sharing invitations register verified grants when accepted. A URL alone
    // must not add an arbitrary document to the personal directory. The
    // desktop window of a sharing World has no `sharing` of its own but the
    // same private catalog, and these repairs would write unprotected copies.
    if (boot.sharing || usesPrivateSessionCatalog(boot.repo)) return
    void (async () => {
      try {
        const { worldId } = await getOrCreateWorld(boot.repo)
        await ensureWorldHomeProject(boot.repo, worldId)
        void ensureWorldSkillPackages(boot.repo, worldId).catch((error) =>
          console.warn('World skill migration will retry on next open', error),
        )
        await addProjectToWorld(boot.repo, worldId, projectId)
        void ensureProjectSkillPackages(boot.repo, worldId, projectId).catch(
          (error) =>
            console.warn(
              'Project skill migration will retry on next open',
              error,
            ),
        )
        // The soul file to the World root, once (RFC 0357 3i).
        void ensureWorldSoulPath(boot.repo, worldId).catch((error) =>
          console.warn('Soul file migration will retry on next open', error),
        )
        void ensureProjectStandingPath(boot.repo, worldId, projectId).catch(
          (error) =>
            console.warn(
              'Project instructions migration will retry on next open',
              error,
            ),
        )
      } catch {
        // offline or unreachable World; nothing to repair here
      }
    })()
  }, [boot.repo, boot.sharing, projectId])

  return <WorldShell projectId={projectId} />
}

/**
 * The old `/w/:projectId`: the same project in this browser's World, at the
 * new address. The old link never said which World, so the pointer answers,
 * as it always did.
 */
function ProjectInWorldRedirect({ projectId }: { projectId: string }) {
  const boot = useTabRepoBoot()
  const navigate = useNavigate()
  const { search } = useLocation()

  useEffect(() => {
    let cancelled = false
    void getOrCreateWorld(boot.repo).then(({ worldId }) => {
      if (!cancelled)
        navigate(`${worldPath(worldId, projectId)}${search}`, { replace: true })
    })
    return () => {
      cancelled = true
    }
  }, [boot.repo, navigate, projectId, search])

  return (
    <div className="flex h-screen items-center justify-center text-muted-foreground text-sm">
      Opening project…
    </div>
  )
}

export function LegacyProjectRedirect() {
  const { projectId } = useParams<{ projectId?: string }>()
  if (!isDocumentSegment(projectId)) return <Navigate to="/" replace />
  return (
    <WithRepo>
      <ProjectInWorldRedirect projectId={projectId as string} />
    </WithRepo>
  )
}

export function WorldPage() {
  const { worldId, projectId } = useParams<{
    worldId?: string
    projectId?: string
  }>()
  const { pathname, search } = useLocation()

  // A path that is not a World is no page of ours; the root is.
  if (worldId !== undefined && !isDocumentSegment(worldId))
    return <Navigate to={`/${search}`} replace />
  if (worldId && projectId !== undefined && !isDocumentSegment(projectId))
    return <Navigate to={`${worldPath(worldId)}${search}`} replace />

  // The entry adopted the URL's World before the first render. Arriving at
  // another one in-page means every reader already holds the old World, so
  // the page starts over on the new one rather than mixing the two.
  // Only when the pointer really moved: a browser refusing storage would
  // otherwise reload forever.
  if (worldId && adoptWorldFromPath(pathname)) {
    window.location.reload()
    return null
  }

  return (
    <WithRepo>
      {projectId ? <Project projectId={projectId} /> : <ProjectRedirect />}
    </WithRepo>
  )
}

function WithRepo({ children }: { children: ReactNode }) {
  return (
    <TabRepoProvider
      relay={SUBDUCTION_RELAY}
      fallback={
        <div className="flex h-screen items-center justify-center text-muted-foreground text-sm">
          Connecting…
        </div>
      }
    >
      <SharingAccountGate>{children}</SharingAccountGate>
    </TabRepoProvider>
  )
}
